if (!customElements.get('predictive-search')) {
  customElements.define(
    'predictive-search',
    class PredictiveSearch extends HTMLElement {
      constructor() {
        super();
        this.cachedMap = new Map();
        this.focusElement = this.input;
        this.searchContent = this.querySelector('.search__content');
        this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';
        this.header = document.querySelector('header');
        this.toggleNavigationButton = document.querySelector('.toggle-navigation-button');

        this.isHeaderSearch = this.closest('header') !== null;

        this.states = {
          OPEN: 'predictive-search-open',
          LOADING: 'btn--loading',
          SEARCH_OPEN: 'search-open'
        };
      }

      connectedCallback() {
        // The header and the search page re-render in the theme editor: the document listeners must go with them.
        this.abortController = new AbortController();
        const { signal } = this.abortController;

        this.resetButton.addEventListener('click', this.clear.bind(this), { signal });
        this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 300), { signal });
        this.input.addEventListener('focus', this.onFocus.bind(this), { signal });
        // LSM: the typed search for OSKAR (lsmTyped below): after a pause, or when a result in the dropdown is tapped
        this.input.addEventListener('input', () => { clearTimeout(this._lsmTypedTimer); this._lsmTypedTimer = setTimeout(() => { if (this.hasAttribute('results')) this.lsmTyped(null); }, 2500); }, { signal });
        this.addEventListener('click', (e) => { const a = e.target.closest('a[href]'); if (a && this.hasAttribute('results')) this.lsmTyped(new URL(a.href, location.origin).pathname); }, { signal, capture: true });
        document.addEventListener('click', this.handleClickOutside.bind(this), { signal });
        this.searchProductTypes?.addEventListener('change', this.handleProductTypeChange.bind(this), { signal });
        this.form?.addEventListener('submit', this.onSubmit.bind(this), { signal });

        /* LSM: on a phone the keyboard steps aside so the results can be seen (David, 2026-10-24: "is there any way to have the
           iphone keyboard hide after a few seconds of inactivity during search?"): when the shopper starts scrolling the results,
           and 3 seconds after the last keystroke once results are showing. Taking focus off the box hides the keyboard and leaves
           the results open (they close only on a tap outside or Escape); a tap on the box brings the keyboard back. */
        if (window.matchMedia('(pointer: coarse)').matches) {
          let idle = null;
          const rest = () => { if (document.activeElement === this.input && this.hasAttribute('results')) this.input.blur(); };
          this.input.addEventListener('input', () => { clearTimeout(idle); idle = setTimeout(rest, 3000); }, { signal });
          this.input.addEventListener('blur', () => clearTimeout(idle), { signal });
          this.addEventListener('touchmove', (e) => { if (e.target !== this.input) rest(); }, { signal, passive: true });
          this.addEventListener('scroll', () => rest(), { signal, capture: true, passive: true });
        }

        document.addEventListener(
          'menu-drawer:open',
          () => {
            this.classList.remove('predictive-search-open');
            document.body.classList.remove('search-open');
          },
          { signal }
        );
      }

      disconnectedCallback() {
        this.abortController?.abort();
      }

      get input() {
        return this.querySelector('input[type="search"]');
      }
      get resetButton() {
        return this.querySelector('button[type="reset"]');
      }

      get searchProductTypes() {
        return this.querySelector('.search__types select');
      }

      get form() {
        return this.querySelector('form');
      }

      get headerCollapseOnScroll() {
        return this.header.dataset.collapseOnScroll === 'true';
      }

      onFocus(event) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
        document.documentElement.style.setProperty(
          '--header-bottom-position',
          `${parseInt(this.header.getBoundingClientRect().bottom)}px`
        );
        if (this.isHeaderSearch && !this.searchRecommendationEmpty) {
          document.body.classList.add('search-open');
          this.toggleNavButtonVisibility(false);
        }

        if (!this.searchRecommendationEmpty) {
          this.searchContent.classList.remove('hidden');
        }
        this.classList.add('predictive-search-open');
        if (this.getQuery().length === 0) {
          if (this.searchRecommendationEmpty) {
            this.searchContent.classList.add('hidden');
          }
          return;
        }
        const url = this.setupURL().toString();
        // Reopening shows the results of a query the buyer already searched: not a search update.
        this.renderSection(url, { isReopen: true });
      }

      getQuery() {
        return this.input.value.trim();
      }

      clear(event = null) {
        event?.preventDefault();
        this.input.value = '';
        this.input.focus();
        this.removeAttribute('results');
        this.toggleSearchState(false);
      }

      handleProductTypeChange(evt) {
        const query = this.getQuery();
        if (query.length > 0) {
          const url = this.setupURL().toString();
          this.renderSection(url);
        }
      }

      /* LSM: a Leica item number ("11695") is a search for that product: the store's SKU is 799429 + the item number + one more digit,
         and Shopify's search does not match part of a SKU, so "11695" found only accessories that name it, not the Summaron-M 28
         itself (David, 2026-10-23). The last digit is not always a barcode check digit (the BP-SCL7 battery, 24026, is 799429240261),
         and OR with plain numbers is unreliable in Shopify's search, so the ten possible SKUs are searched as SKU fields:
         "variants.sku:799429116950 OR ... OR variants.sku:799429116959" (79 of 79 item numbers tested found their product). */
      lsmItemQuery(q) {
        const n = String(q || '').trim(); if (!/^\d{5}$/.test(n)) return null
        return Array.from({ length: 10 }, (_, d) => `variants.sku:799429${n}${d}`).join(' OR ')
      }

      /* LSM (2026-10-26, from the searches that found nothing): the names people type that the titles spell differently.
         "m240" / "typ240" / "m typ 240" -> "Typ 240" (titles say "Leica M (Typ 240)"); "d-lux8" / "dlux 8" -> "D-Lux 8" (also V-Lux, C-Lux). */
      lsmRewrite(q) {
        const o = String(q || '').trim(); if (!o) return null
        let s = o.replace(/\b([dvc])[\s-]?lux[\s-]?(\d+)\b/gi, (m, a, b) => `${a.toUpperCase()}-Lux ${b}`)
        s = s.replace(/\b(?:m\s*)?\(?\s*typ\s*\(?\s*(\d{3})\s*\)?/gi, 'Typ $1').replace(/\bm\s?(2\d\d)\b/gi, 'Typ $1')
        s = s.replace(/\belpro\s*e?\s?(\d{2})\b/gi, 'ELPRO E$1')   // "elpro 52" -> the ELPRO E52 set (2026-10-26)
        return s !== o ? s : null
      }

      /* a question about the store, not a product ("payment methods", "return policy", "store hours"): Enter opens the page that answers it */
      lsmInfoPage(q) {
        const s = String(q || '').toLowerCase().trim(); if (!s || s.split(/\s+/).length > 4) return null
        if (/\b(payments?|payment methods?|pay(ing)? with|financ\w*|affirm|klarna|bread|shipping|delivery|returns?|refunds?|exchanges?|return policy|warranty|faq)\b/.test(s)) return '/pages/faq'
        if (/\b(hours|opening|open today|address|location|directions|parking|visit us|where are you)\b/.test(s)) return '/pages/visit-us'
        return null
      }

      /* what was typed, for OSKAR (scripts/shopify_pixel.js passes 'lsm_search_typed' on): once the dropdown has settled for 2.5 s,
         or when a result is tapped; never twice for the same words; nothing when the search is sent with Enter (Shopify reports that) */
      lsmTyped(clicked) {
        const query = this.getQuery(); if (query.length < 2) return
        clearTimeout(this._lsmTypedTimer)
        if (!clicked && this._lsmTypedSent === query) return
        this._lsmTypedSent = query
        let results = null; try { results = this.getTotalResults(); } catch (e) { /* not counted */ }
        try { window.Shopify?.analytics?.publish?.('lsm_search_typed', { query: query.slice(0, 200), results, clicked: clicked || null }); } catch (e) { /* never break search */ }
      }

      onSubmit(event) {
        this._lsmTypedSent = this.getQuery(); clearTimeout(this._lsmTypedTimer)
        const lsmInfo = this.lsmInfoPage(this.getQuery())
        if (lsmInfo && !this.searchProductTypes?.value) { event.preventDefault(); window.location.href = lsmInfo; return }
        const lsmSyn = this.lsmRewrite(this.getQuery())
        if (lsmSyn && !this.searchProductTypes?.value) { event.preventDefault(); const params = new URLSearchParams(); params.set('q', lsmSyn); window.location.href = `${this.form.getAttribute('action')}?${params.toString()}`; return }
        const lsmItem = this.lsmItemQuery(this.getQuery())
        if (lsmItem && !this.searchProductTypes?.value) { event.preventDefault(); const params = new URLSearchParams(); params.set('q', lsmItem); window.location.href = `${this.form.getAttribute('action')}?${params.toString()}`; return }
        const productType = this.searchProductTypes?.value;
        if (!productType) return;
        const query = this.getQuery();
        if (query.length === 0) return;
        if (query.startsWith('product_type:')) return;
        event.preventDefault();
        const params = new URLSearchParams();
        params.set('q', `product_type:${productType} AND ${query}`);
        params.set('options[prefix]', 'last');
        window.location.href = `${this.form.getAttribute('action')}?${params.toString()}`;
      }

      setupURL() {
        // LSM: the page's own address, not shopUrl: in a local preview shopUrl is the store's domain and the browser refuses the request
        const url = new URL(FoxTheme.routes.predictive_search_url, window.location.origin);
        let search_term = this.getQuery();
        // LSM: a bare model code ("q2", "m6", "sl3", "m10-r", "q2 monochrom") is searched as "Leica q2": Shopify's search ranks the cameras
        // themselves first that way, and for "q2" alone it returned only accessories, so the archive Q2 never showed (David, 2026-10-22)
        // only Leica's own model letters (M, MP, MA, ME, Q, SL, S, CL, TL, T, X, R, C, D, V): "rz67" is a Mamiya, searched as typed (2026-10-26)
        if (/^(m|mp|ma|me|q|sl|s|cl|tl|t|x|r|c|d|v)-?\d+[a-z0-9-]*(\s|$)/i.test(search_term.trim()) && !/^leica\b/i.test(search_term.trim())) search_term = `Leica ${search_term.trim()}`;
        const lsmSyn = this.lsmRewrite(search_term); if (lsmSyn) search_term = /^leica\b/i.test(lsmSyn) ? lsmSyn : `Leica ${lsmSyn}`;
        const lsmItem = this.lsmItemQuery(search_term); if (lsmItem) search_term = lsmItem;
        if (this.searchProductTypes && this.searchProductTypes.value != '') {
          search_term = `product_type:${this.searchProductTypes.value} AND ${encodeURIComponent(search_term)}`;
        }
        return (
          url.searchParams.set('q', search_term),
          url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3),
          url.searchParams.set('resources[limit_scope]', 'each'),
          url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this)),
          url
        );
      }

      onChange() {
        if (this.getQuery().length === 0) {
          this.clear();
          return;
        }
        const url = this.setupURL().toString();
        this.renderSection(url);
      }

      renderSection(url, { isReopen = false } = {}) {
        const searchUpdate = isReopen
          ? null
          : FoxTheme.storefrontEvents?.start(this, 'SearchUpdateEvent', {
              search: { query: this.getQuery() }
            });

        this.cachedMap.has(url)
          ? this.renderSectionFromCache(url, searchUpdate)
          : this.renderSectionFromFetch(url, searchUpdate);
      }

      renderSectionFromCache(url, searchUpdate) {
        const responseText = this.cachedMap.get(url);
        (this.renderSearchResults(responseText), this.setAttribute('results', ''));
        searchUpdate?.resolve({ totalCount: this.getTotalResults() });
      }

      renderSectionFromFetch(url, searchUpdate) {
        this.setLoadingState(true);

        fetch(url)
          .then((response) => {
            if (!response.ok) throw new Error('Network response was not ok');
            return response.text();
          })
          .then((responseText) => {
            this.renderSearchResults(responseText);
            this.cachedMap.set(url, responseText);
            searchUpdate?.resolve({ totalCount: this.getTotalResults() });
          })
          .catch((error) => {
            searchUpdate?.reject(error);
            console.error('Error fetching data: ', error);
            this.setAttribute('error', 'Failed to load data');
          })
          .finally(() => this.setLoadingState(false));
      }

      getTotalResults() {
        const results = document.getElementById(`PredictiveSearchResults-${FoxTheme.utils.getSectionId(this)}`);
        return Number(results?.querySelector('[data-total-results]')?.dataset.totalResults) || 0;
      }
      renderSearchResults(responseText) {
        const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
        const targetElement = document.getElementById(id);

        if (targetElement) {
          const parser = new DOMParser();
          const parsedDoc = parser.parseFromString(responseText, 'text/html');
          const contentElement = parsedDoc.getElementById(id);

          if (contentElement) {
            this.searchContent?.classList.remove('hidden');
            targetElement.innerHTML = contentElement.innerHTML;

            if (this.isHeaderSearch && !document.body.classList.contains('search-open')) {
              document.body.classList.add('search-open');
              this.toggleNavButtonVisibility(true);
              this.classList.add('predictive-search-open');
            } else {
              this.classList.add('predictive-search-open');
            }
          } else {
            console.error(`Element with id '${id}' not found in the parsed response.`);
          }
        } else {
          console.error(`Element with id '${id}' not found in the document.`);
        }
      }

      handleClickOutside(event) {
        const target = event.target;
        const shouldClose = this.isHeaderSearch
          ? !this.contains(target) &&
            ((target.classList.contains('fixed-overlay') && target.closest('.header-section')) ||
              target.classList.contains('header__search-close'))
          : !this.contains(target);

        if (shouldClose) {
          setTimeout(() => this.toggleSearchState(false));
        }
      }

      toggleSearchState(isOpen) {
        this.classList.toggle(this.states.OPEN, isOpen);
        if (this.isHeaderSearch) {
          document.body.classList.toggle(this.states.SEARCH_OPEN, isOpen);
          this.toggleNavButtonVisibility(!isOpen);
        }
        if (!isOpen && this.searchRecommendationEmpty) {
          this.searchContent?.classList.add('hidden');
        }
      }

      toggleNavButtonVisibility(isOpen) {
        this.toggleNavigationButton &&
          this.headerCollapseOnScroll &&
          this.toggleNavigationButton.classList.toggle('is-show', isOpen);
      }

      setLoadingState(isLoading) {
        if (isLoading) {
          this.setAttribute('loading', 'true');
          this.resetButton.classList.add(this.states.LOADING);
        } else {
          this.removeAttribute('loading');
          this.resetButton.classList.remove(this.states.LOADING);
          this.setAttribute('results', 'true');
        }
      }
    }
  );
}
